import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { useAuth } from '../context/AuthContext';
import { FREE_USES } from '../lib/access';
import { IconCheck, IconSparkle } from '../components/Icons';

interface RegionResponse {
  country: string;
  currency: 'GBP' | 'USD';
  prices: { monthly: string; annual: string };
}

// Shown when a signed-in user has no active subscription. Two options: Monthly £3.99
// or Annual £39.99. 7-day free trial on both. Clicking "Subscribe" calls our
// /api/stripe/create-checkout-session endpoint which returns a Stripe Checkout URL.
// Default display prices when the region endpoint hasn't responded yet (or fails).
// Matches GBP because that's the larger market and the default for anyone not US.
const DEFAULT_REGION: RegionResponse = {
  country: 'GB',
  currency: 'GBP',
  prices: { monthly: '£3.99', annual: '£39.95' },
};

export function PaywallScreen() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [busy, setBusy] = useState<'monthly' | 'annual' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [region, setRegion] = useState<RegionResponse>(DEFAULT_REGION);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/region')
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data: RegionResponse) => {
        if (cancelled) return;
        setRegion(data);
      })
      .catch(() => {
        // Stay on the default. The user gets GBP prices, the checkout endpoint
        // independently picks the right currency at checkout time anyway.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function startCheckout(plan: 'monthly' | 'annual') {
    setBusy(plan);
    setError(null);
    try {
      const response = await fetch('/api/stripe/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan, email: user?.email, userId: user?.id }),
      });
      if (!response.ok) {
        const detail = (await response.json().catch(() => ({}))) as { error?: string };
        throw new Error(detail.error || `Checkout failed (${response.status}).`);
      }
      const json = (await response.json()) as { url?: string };
      if (!json.url) throw new Error('Checkout did not return a URL.');
      window.location.href = json.url;
    } catch (e) {
      setBusy(null);
      setError((e as Error).message);
    }
  }

  return (
    <AppShell title="Subscribe">
      <div className="px-5 pt-2 pb-6">
        <section className="py-4 text-center">
          <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-accent font-medium">
            <IconSparkle size={14} /> Keep going
          </span>
          <h1 className="mt-3 text-3xl font-bold tracking-tight text-ink">
            You've finished your {FREE_USES} free worksheets.
          </h1>
          <p className="mt-3 text-sm text-ink/65 leading-relaxed max-w-md mx-auto">
            Hopefully homework feels different already. Unlock unlimited worksheets to keep
            the routine going - plus every future tool we ship. Cancel any time. Designed
            by a dyslexia parent.
          </p>
        </section>

        {error && (
          <div role="alert" className="bg-warm border border-amber-300 text-ink rounded-xl p-4 text-sm mb-4">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <PlanCard
            plan="monthly"
            label="Monthly"
            price={region.prices.monthly}
            cadence="/ month"
            note="Cancel any time. Billed monthly."
            busy={busy === 'monthly'}
            onChoose={() => startCheckout('monthly')}
          />
          <PlanCard
            plan="annual"
            label="Yearly"
            price={region.prices.annual}
            cadence="/ year"
            note="Roughly 2 months free vs monthly. Cancel any time."
            recommended
            busy={busy === 'annual'}
            onChoose={() => startCheckout('annual')}
          />
        </div>

        <ul className="mt-6 space-y-2 text-sm text-ink/70 leading-relaxed">
          <li className="flex gap-2">
            <span className="text-accent flex-shrink-0 mt-0.5">
              <IconCheck size={16} />
            </span>
            Photo any worksheet. Dictate or type answers. Save as a PDF.
          </li>
          <li className="flex gap-2">
            <span className="text-accent flex-shrink-0 mt-0.5">
              <IconCheck size={16} />
            </span>
            Every future tool (Read Assist, Spell Assist, Letter Assist) included as soon as it ships.
          </li>
          <li className="flex gap-2">
            <span className="text-accent flex-shrink-0 mt-0.5">
              <IconCheck size={16} />
            </span>
            Designed for dyslexia. Calm voice. No body images. Made by a dyslexia parent.
          </li>
        </ul>

        <div className="mt-8 pt-4 border-t border-ink/5 text-center">
          <p className="text-xs text-ink/50">
            Signed in as <strong className="text-ink/70">{user?.email}</strong>.
          </p>
          <button
            onClick={async () => {
              await signOut();
              navigate('/');
            }}
            className="mt-2 text-xs text-ink/50 hover:text-ink underline"
          >
            Sign out
          </button>
        </div>
      </div>
    </AppShell>
  );
}

function PlanCard({
  label,
  price,
  cadence,
  note,
  recommended = false,
  busy,
  onChoose,
}: {
  plan: 'monthly' | 'annual';
  label: string;
  price: string;
  cadence: string;
  note: string;
  recommended?: boolean;
  busy: boolean;
  onChoose: () => void;
}) {
  return (
    <div
      className={`rounded-2xl p-5 ${
        recommended ? 'bg-accent text-white shadow-lg shadow-accent/20' : 'bg-white border border-ink/10'
      }`}
    >
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <p className={`text-xs uppercase tracking-wider ${recommended ? 'text-white/70' : 'text-ink/50'}`}>
            {label}
            {recommended && <span className="ml-2 inline-block bg-white/15 px-1.5 py-0.5 rounded-full text-[10px]">Recommended</span>}
          </p>
          <p className="mt-1 text-3xl font-bold">
            {price}
            <span className={`text-base font-medium ${recommended ? 'text-white/70' : 'text-ink/55'}`}> {cadence}</span>
          </p>
          <p className={`mt-1 text-xs ${recommended ? 'text-white/75' : 'text-ink/55'}`}>{note}</p>
        </div>
      </div>
      <button
        onClick={onChoose}
        disabled={busy}
        className={`mt-5 w-full inline-flex items-center justify-center px-5 py-3 rounded-full font-medium transition ${
          recommended
            ? 'bg-white text-accent hover:brightness-95 disabled:opacity-70'
            : 'bg-accent text-white hover:brightness-105 disabled:opacity-70'
        }`}
      >
        {busy ? 'Opening checkout...' : 'Subscribe'}
      </button>
    </div>
  );
}
